import { type ClientIndex, portCapacity, usedPorts } from './connections.ts';
import {
  CAPACITY_BUSY_FRACTION,
  CAPACITY_FULL_FRACTION,
  CAPACITY_LIGHT_FRACTION,
} from './constants.ts';
import { walkRooms } from './tree.ts';
import type { Connection } from './schemas/connection.ts';
import type { RoomTree } from './schemas/responses.ts';

// How full a thing is, as one word and one colour (ROADMAP D11).
//
// A room's devices, a room's ports, a rack's units, a switch's PoE budget: the
// figures come from four different places and each of them already has a
// module that works them out (shared/summary.ts, shared/connections.ts,
// shared/rack-layout.ts, shared/poe.ts). What was missing was one scale to read
// them against, so a full rack and a full patch panel are the same colour
// wherever they are drawn, and one legend explains both.
//
// Nothing here counts anything. It takes a used-of-total pair and answers which
// band it falls in; a total nobody has recorded answers `unknown`, because "we
// do not know how big this is" is not the same as "it is empty", and colouring
// it as empty would be the map telling a comfortable lie.
//
// Pure: no DOM, no fetch, no colour of its own — the bands name CSS custom
// properties the app and the report each resolve for themselves.

/** How full something is, in the words the legend uses. */
export const CAPACITY_BANDS = ['unknown', 'empty', 'light', 'busy', 'full', 'over'] as const;
export type CapacityBand = (typeof CAPACITY_BANDS)[number];

/** What each band is called wherever one is shown or keyed. */
export const CAPACITY_BAND_LABELS: Readonly<Record<CapacityBand, string>> = {
  unknown: 'Size not recorded',
  empty: 'Empty',
  light: 'Room to spare',
  busy: 'Filling up',
  full: 'Nearly full',
  over: 'Over capacity',
};

/**
 * Which band a used-of-total figure falls in. `total` of null or zero is
 * `unknown`: a rack whose height was never set, a switch with no PoE budget
 * recorded, a room with nothing to be full of. Anything past the total is
 * `over`, which is a real state — a rack can hold more than it is rated for,
 * and the map should say so rather than clamping it to full.
 */
export function capacityBand(used: number, total: number | null): CapacityBand {
  if (total === null || total <= 0) {
    return 'unknown';
  }
  if (used <= 0) {
    return 'empty';
  }
  const fraction = used / total;
  if (fraction > 1) {
    return 'over';
  }
  if (fraction >= CAPACITY_FULL_FRACTION) {
    return 'full';
  }
  if (fraction >= CAPACITY_BUSY_FRACTION) {
    return 'busy';
  }
  return fraction >= CAPACITY_LIGHT_FRACTION ? 'light' : 'empty';
}

/**
 * The band a plain count falls in, for a measure with no ceiling of its own —
 * how many devices are in a room. `busiest` is the largest count among the
 * things being compared, so the colours read against each other rather than
 * against a number nobody chose. Nothing to compare against is `unknown`.
 */
export function countBand(count: number, busiest: number): CapacityBand {
  return busiest <= 0 ? 'unknown' : capacityBand(count, busiest);
}

/** "8 of 12U", "3 of 4", "—" when the total was never recorded. */
export function capacityText(used: number, total: number | null, unit = ''): string {
  return total === null || total <= 0 ? '—' : `${String(used)} of ${String(total)}${unit}`;
}

/** One row of the capacity key: a band and the words that explain it. */
export interface CapacityLegendRow {
  readonly band: CapacityBand;
  readonly label: string;
}

/**
 * The one capacity key, used by the plan and by the racks list so a colour
 * means the same thing on both. `unknown` is left out: it is the absence of a
 * figure rather than a level of fullness, and a key that listed it would
 * invite someone to read grey as a measurement.
 */
export const CAPACITY_LEGEND: readonly CapacityLegendRow[] = CAPACITY_BANDS.filter(
  (band) => band !== 'unknown',
).map((band) => ({ band, label: CAPACITY_BAND_LABELS[band] }));

/** How full one room is, by the two measures a plan can colour it with. */
export interface RoomCapacity {
  readonly roomId: string;
  /** Devices in the room, its racks included. */
  readonly devices: number;
  /** Ports in use on those devices, and how many they declare; null when none declares any. */
  readonly portsUsed: number;
  readonly portsTotal: number | null;
}

/**
 * How full each room of a property is (ROADMAP D11), keyed by room id. Every
 * figure comes from the module that already works it out — the tree walk for
 * what is in a room, `portCapacity` for how many ports a device declares, and
 * `usedPorts` for how many of them are taken — so the plan's colours and the
 * device pages never disagree about the same number.
 *
 * A room whose devices declare no ports at all has a null port total: it is a
 * room ports are not a measure of, not a room with none in use.
 */
export function roomCapacities(
  rooms: readonly RoomTree[],
  index: ClientIndex,
  connections: readonly Connection[],
): Map<string, RoomCapacity> {
  const capacities = new Map<string, RoomCapacity>();
  walkRooms(rooms, '', (tree) => {
    const devices = [...tree.items, ...tree.racks.flatMap((rack) => rack.items)];
    let portsUsed = 0;
    let declared = 0;
    let anyPorts = false;
    for (const device of devices) {
      const capacity = portCapacity(device);
      if (capacity === null) {
        continue;
      }
      anyPorts = true;
      declared += capacity;
      portsUsed += usedPorts(index, connections, device.id).size;
    }
    capacities.set(tree.room.id, {
      roomId: tree.room.id,
      devices: devices.length,
      portsUsed,
      portsTotal: anyPorts ? declared : null,
    });
  });
  return capacities;
}
